import { getRewardViewModel, parseRewardTiers } from './zp-cart-experience-core.js';

const shippingStates = new Map();
let pendingRecommendationAction = null;
let recommendationErrorTimer;

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const component = form.closest('product-form-component[data-zp-cart-recommendation]');
    if (!component) return;
    if (pendingRecommendationAction) return;
    pendingRecommendationAction = {
      productId: component.dataset.productId || '',
      message: '',
    };
  },
  true
);

document.addEventListener('cart:error', (event) => {
  if (!pendingRecommendationAction) return;
  pendingRecommendationAction.message = event.detail && event.detail.data ? event.detail.data.message || '' : '';
});

function restoreRecommendationFocusAndError(didError) {
  if (!pendingRecommendationAction) return;
  const action = pendingRecommendationAction;
  pendingRecommendationAction = null;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const drawer = document.querySelector('.cart-drawer__dialog[open]');
      if (!drawer) return;

      if (action.message) {
        const alert = drawer.querySelector('[data-zp-cart-alert]');
        if (alert) {
          alert.textContent = action.message;
          if (recommendationErrorTimer) window.clearTimeout(recommendationErrorTimer);
          recommendationErrorTimer = window.setTimeout(() => {
            if (alert.isConnected) alert.textContent = '';
          }, 10000);
        }
      }

      let focusTarget = null;
      if (didError) {
        const card = Array.from(drawer.querySelectorAll('[data-zp-recommendation-product]')).find(
          (element) => element.dataset.zpRecommendationProduct === action.productId
        );
        focusTarget = card ? card.querySelector('button:not([disabled])') : null;
      } else {
        const row = Array.from(drawer.querySelectorAll('[data-zp-cart-product-id]')).find(
          (element) => element.dataset.zpCartProductId === action.productId
        );
        focusTarget = row ? row.querySelector('button:not([disabled]), input:not([disabled]), a[href]') : null;
      }

      if (!focusTarget) {
        focusTarget = drawer.querySelector('.zp-cart-recommendations__heading, .zp-cart-recommendation-card button:not([disabled])');
        if (focusTarget && !focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
      }
      if (focusTarget) focusTarget.focus();
    });
  });
}

document.addEventListener('zp:recommendation-synced', (event) => {
  const didError = Boolean(event.detail && event.detail.didError);
  restoreRecommendationFocusAndError(didError);
});

class ZpCartRewards extends HTMLElement {
  #observer = new MutationObserver(() => this.#render());
  #pendingButton = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#observer.disconnect();
  }

  #handleClick = async (event) => {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-zp-reward-action]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled || this.#pendingButton) return;

    const mounts = Array.from(this.querySelectorAll('[data-zp-reward-mount]'));
    const config = this.querySelector('[data-zp-reward-config]');
    const tiers = config ? parseRewardTiers(config.textContent || '') : null;
    if (!tiers || mounts.length === 0) return;

    const quantity = this.#getQuantity(mounts);
    const viewModel = getRewardViewModel(tiers, quantity);
    const gap = Math.max(0, viewModel.target - viewModel.current);
    const line = Number(button.dataset.zpLine);
    const lineQuantity = Number(button.dataset.zpLineQuantity);
    const cartItems = this.closest('cart-items-component');
    if (
      gap === 0 ||
      button.dataset.zpCanUpdate !== 'true' ||
      !Number.isSafeInteger(line) ||
      line < 1 ||
      !Number.isSafeInteger(lineQuantity) ||
      lineQuantity < 1 ||
      !cartItems ||
      typeof cartItems.updateQuantity !== 'function'
    ) {
      return;
    }

    this.#pendingButton = button;
    this.#render();

    try {
      await cartItems.updateQuantity({
        line,
        quantity: lineQuantity + gap,
        action: 'reward-fill',
      });
    } catch (error) {
      console.error('[cart-reward] Failed to update quantity', error);
    } finally {
      if (this.isConnected) {
        this.#pendingButton = null;
        this.#render();
      }
    }
  };

  #observe() {
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-zp-quantity'],
      childList: true,
      subtree: true,
    });
  }

  #getQuantity(mounts) {
    return mounts.reduce((total, mount) => {
      const value = Number(mount.dataset.zpQuantity);
      return total + (Number.isSafeInteger(value) && value > 0 ? value : 0);
    }, 0);
  }

  #render() {
    this.#observer.disconnect();
    try {
      const config = this.querySelector('[data-zp-reward-config]');
      const mounts = Array.from(this.querySelectorAll('[data-zp-reward-mount]'));
      if (!config || mounts.length === 0) return;

      const tiers = parseRewardTiers(config.textContent || '');
      if (!tiers) return;

      const quantity = this.#getQuantity(mounts);
      const viewModel = getRewardViewModel(tiers, quantity);
      const gap = Math.max(0, viewModel.target - viewModel.current);

      const highestTier = tiers[tiers.length - 1];
      const showSharedNote = quantity < highestTier.num;

      for (const [index, mount] of mounts.entries()) {
        const message = mount.querySelector('[data-zp-reward-message]');
        const count = mount.querySelector('[data-zp-reward-count]');
        const progress = mount.querySelector('[data-zp-reward-progress]');
        const fill = mount.querySelector('[data-zp-reward-fill]');
        const note = mount.querySelector('.zp-cart-reward__note');
        const button = mount.querySelector('[data-zp-reward-action]');
        if (!message || !count || !progress || !fill || !(button instanceof HTMLButtonElement)) continue;

        message.textContent = viewModel.message;
        count.textContent = `${viewModel.current}/${viewModel.target}`;
        progress.setAttribute('aria-valuenow', String(Math.min(viewModel.current, viewModel.target)));
        progress.setAttribute('aria-valuemax', String(viewModel.target));
        fill.style.width = `${viewModel.percent}%`;
        const isComplete = gap === 0;
        const isPending = this.#pendingButton === button;
        button.disabled = Boolean(this.#pendingButton) || isComplete || button.dataset.zpCanUpdate !== 'true';
        button.dataset.zpComplete = String(isComplete);
        button.dataset.zpPending = String(isPending);
        button.setAttribute('aria-busy', String(isPending));
        if (note) note.hidden = index !== 0 || !showSharedNote;
        mount.hidden = false;
      }

      const status = this.querySelector('[data-zp-reward-status]');
      if (status) status.textContent = `${viewModel.message} ${viewModel.current}/${viewModel.target}`;
    } catch (error) {
      console.error('[cart-reward] Failed to initialize', error);
    } finally {
      if (this.isConnected) this.#observe();
    }
  }
}

if (!customElements.get('zp-cart-rewards')) {
  customElements.define('zp-cart-rewards', ZpCartRewards);
}

class ZpCartShippingState extends HTMLElement {
  #drawer;
  #observer = new MutationObserver(() => this.#observeServerState());

  connectedCallback() {
    this.#drawer = this.closest('cart-drawer-component');
    if (this.#drawer) this.#drawer.addEventListener('dialog:open', this.#handleDrawerOpen);
    this.#observeServerState();
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-zp-valid', 'data-zp-unlocked', 'data-zp-empty', 'data-zp-identity'],
    });
  }

  disconnectedCallback() {
    if (this.#drawer) this.#drawer.removeEventListener('dialog:open', this.#handleDrawerOpen);
    this.#observer.disconnect();
  }

  #observeServerState() {
    if (this.dataset.zpValid !== 'true' || !this.dataset.zpIdentity) return;
    const identity = this.dataset.zpIdentity;
    const current = this.dataset.zpEmpty === 'true' || this.dataset.zpUnlocked !== 'true' ? 'below' : 'above';
    const state = shippingStates.get(identity);

    if (!state) {
      shippingStates.set(identity, { level: current, pending: false });
      return;
    }

    if (current === 'below') {
      state.level = 'below';
      state.pending = false;
      return;
    }

    if (state.level === 'below') {
      state.level = 'above';
      if (this.#isDrawerOpen()) this.#playConfetti(state);
      else state.pending = true;
    } else if (state.pending && this.#isDrawerOpen()) {
      this.#playConfetti(state);
    }
  }

  #handleDrawerOpen = () => {
    const state = shippingStates.get(this.dataset.zpIdentity);
    if (!state || !state.pending || state.level !== 'above') return;
    this.#playConfetti(state);
  };

  #isDrawerOpen() {
    const dialog = this.closest('dialog[open]');
    return Boolean(dialog);
  }

  #playConfetti(state) {
    state.pending = false;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const dialog = this.closest('.cart-drawer__dialog');
    if (!dialog) return;
    if (dialog.querySelector('[data-zp-confetti]')) {
      state.pending = true;
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'zp-cart-confetti';
    overlay.dataset.zpConfetti = '';
    overlay.setAttribute('aria-hidden', 'true');

    const particleCount = 36;
    for (let index = 0; index < particleCount; index += 1) {
      const particle = document.createElement('span');
      const size = 5 + ((index * 3) % 5);
      particle.style.setProperty('--zp-confetti-index', String(index));
      particle.style.setProperty('--zp-confetti-x', `${(index * 37) % 100}%`);
      particle.style.setProperty('--zp-confetti-delay', `${((index * 13) % 60) * 10}ms`);
      particle.style.setProperty('--zp-confetti-duration', `${1600 + (((index * 7) % 10) * 100)}ms`);
      particle.style.setProperty('--zp-confetti-width', `${size}px`);
      particle.style.setProperty('--zp-confetti-height', `${size * 2}px`);
      particle.style.setProperty('--zp-confetti-drift', `${((index * 17) % 80) - 40}px`);
      particle.style.setProperty('--zp-confetti-rotate', `${360 + ((index * 47) % 360)}deg`);
      overlay.appendChild(particle);
    }

    dialog.appendChild(overlay);
    let finishedParticles = 0;
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      overlay.remove();
      if (state.pending && state.level === 'above' && this.#isDrawerOpen()) this.#playConfetti(state);
    };
    overlay.addEventListener('animationend', (event) => {
      if (event.target instanceof HTMLSpanElement) finishedParticles += 1;
      if (finishedParticles >= particleCount) cleanup();
    });
    window.setTimeout(cleanup, 3200);
  }
}

if (!customElements.get('zp-cart-shipping-state')) {
  customElements.define('zp-cart-shipping-state', ZpCartShippingState);
}
